import React from "react";
import { Stack, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import FormSwitchField from "../components/common/FormSwitchField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch } from "../redux/store";
import {
  createCompanyCharge,
  updateCompanyCharge,
} from "../redux/actions/companyChargeActions";
import { cleanFormData } from "../helpers/formHelpers";
import { companyChargeActions } from "../redux/slices/companyCharge";

import { CompanyChargeRequestDto } from "../shared/dtos/companyCharge/CompanyChargeRequestDto";
import { CompanyChargeResponseDto } from "../shared/dtos/companyCharge/CompanyChargeResponseDto";
import { ChargeType } from "../shared/enums/ChargeType";

const chargeTypeOptions = [
  {
    value: ChargeType.EXCLUSIVE,
    label: "Exclusive (Added on top of subtotal)",
  },
  { value: ChargeType.INCLUSIVE, label: "Inclusive (Extracted from subtotal)" },
];

const companyChargeSchema = Yup.object().shape({
  name: Yup.string()
    .required("Charge name is required (e.g., Shipping Handling Fee)")
    .max(100, "Name is too long"),
  rate: Yup.number()
    .required("Rate is required")
    .min(0, "Rate cannot be negative"),
  chargeType: Yup.string().required("Charge type is required"),
  isTaxable: Yup.boolean(),
});

interface CreateOrUpdateCompanyChargeFormProps {
  companyCharge?: CompanyChargeResponseDto | null;
  isEditing?: boolean;
  loading: boolean;
  error: string | null;
}

const CreateOrUpdateCompanyChargeForm: React.FC<
  CreateOrUpdateCompanyChargeFormProps
> = ({
  companyCharge = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  const initialValues: Partial<CompanyChargeRequestDto> = {
    name: companyCharge?.name || "",
    rate: companyCharge?.rate || ("" as unknown as number),
    chargeType: companyCharge?.chargeType || ChargeType.EXCLUSIVE,
    isTaxable: companyCharge?.isTaxable || false,
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() =>
          dispatch(
            isEditing
              ? companyChargeActions.setErrorUpdate(null)
              : companyChargeActions.setErrorCreate(null),
          )
        }
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={companyChargeSchema}
        onSubmit={(values) => {
          const cleanedValues = cleanFormData(
            values,
          ) as unknown as Partial<CompanyChargeRequestDto>;

          const payload: CompanyChargeRequestDto = {
            ...cleanedValues,
            name: cleanedValues.name as string,
            chargeType: cleanedValues.chargeType as ChargeType,
            rate: Number(cleanedValues.rate),
            isTaxable: Boolean(cleanedValues.isTaxable),
          };

          if (isEditing && companyCharge) {
            dispatch(
              updateCompanyCharge({ id: companyCharge.id, values: payload }),
            );
          } else {
            dispatch(createCompanyCharge(payload));
          }
        }}
      >
        {({
          dirty,
          isValid,
        }: FormikProps<Partial<CompanyChargeRequestDto>>) => (
          <Form>
            <Stack spacing={3}>
              <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "1fr" }}>
                <FormTextField
                  name="name"
                  label="Charge Name"
                  placeholder="e.g., Restocking Fee"
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                  alignItems: "center",
                }}
              >
                <FormTextField
                  name="rate"
                  label="Rate (%)"
                  type="number"
                  placeholder="e.g., 10.00"
                />
                <FormSelectField
                  name="chargeType"
                  label="Charge Type"
                  options={chargeTypeOptions}
                />
                <Box
                  sx={{ display: "flex", alignItems: "center", height: "100%" }}
                >
                  <FormSwitchField name="isTaxable" label="Is Taxable?" />
                </Box>
              </Box>

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Charge"}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
                }
                fullWidth
                sx={{ py: 1.5, fontWeight: 700 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateOrUpdateCompanyChargeForm;
